import { useEffect, useState, FormEvent } from 'react'
import { tenantConfig } from '../config/tenant'
import { api } from '../api/client'

/** Calculator context carried into the lead (snake_case to match the backend). */
export interface LeadContext {
  property_price?: number
  deposit?: number
  loan_amount?: number
  lvr?: number
  interest_rate?: number
  term_years?: number
  state?: string
  first_home_buyer?: boolean
  monthly_repayment?: number
  stamp_duty?: number
  lmi?: number
  // Extra-repayments + offset figures (calculator "Get my results").
  extra_repayment_amount?: number
  extra_repayment_frequency?: string
  extra_interest_saved?: number
  extra_time_saved?: string
  offset_balance?: number
  offset_interest_saved?: number
  offset_time_saved?: string
  // Which playbook was requested (Playbooks hub).
  playbook?: string
  // Property-report lead (home page): the address a customer wants a report on.
  property_address?: string
  // Tag to distinguish enquiry types that share a source (e.g. refinance savings).
  enquiry_type?: string
}

interface Props {
  open: boolean
  onClose: () => void
  source?: 'website' | 'calculator' | 'playbook' | 'referral' | 'other' | 'property_report'
  /** When opened from the calculator, the customer's figures flow through here. */
  context?: LeadContext
  /** Optional overrides so the same modal can present as "Get my results". */
  heading?: string
  blurb?: string
  submitLabel?: string
  /** Called once after a lead is successfully created (e.g. to download a PDF). */
  onSubmitted?: (payload: { name: string; email: string; phone: string; context?: LeadContext }) => void
  /** Show a required "Property address" field (used by the free property-report CTA). */
  addressField?: boolean
  addressLabel?: string
}

interface LeadResponse {
  id: string
  status: string
}

const AUD = (v?: number) =>
  v == null ? '' : v.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 })

/** Human-readable summary of the calculator context, for the notes field. */
function summariseContext(c: LeadContext): string {
  const lines: string[] = []
  if (c.property_address) lines.push(`Property address: ${c.property_address}`)
  if (c.enquiry_type) lines.push(`Enquiry: ${c.enquiry_type}`)
  if (c.property_price) lines.push(`Property price: ${AUD(c.property_price)}`)
  if (c.deposit != null) lines.push(`Deposit: ${AUD(c.deposit)}`)
  if (c.loan_amount) lines.push(`Loan amount: ${AUD(c.loan_amount)}`)
  if (c.lvr != null) lines.push(`LVR: ${c.lvr.toFixed(1)}%`)
  if (c.interest_rate) lines.push(`Rate used: ${c.interest_rate.toFixed(2)}%`)
  if (c.playbook) lines.push(`Requested: ${c.playbook}`)
  if (c.state) lines.push(`State: ${c.state}`)
  if (c.monthly_repayment) lines.push(`Est. repayment: ${AUD(c.monthly_repayment)}/mo`)
  if (c.extra_repayment_amount) {
    lines.push(
      `Extra repayments: ${AUD(c.extra_repayment_amount)} ${c.extra_repayment_frequency ?? ''}`.trim() +
        ` (saves ${AUD(c.extra_interest_saved)}, ${c.extra_time_saved ?? ''})`.trimEnd(),
    )
  }
  if (c.offset_balance) {
    lines.push(
      `Offset balance: ${AUD(c.offset_balance)} (saves ${AUD(c.offset_interest_saved)}, ${c.offset_time_saved ?? ''})`,
    )
  }
  return lines.join('\n')
}

const PURPOSES = [
  { value: 'first_home', label: 'First home' },
  { value: 'purchase', label: 'Next home / upgrade' },
  { value: 'refinance', label: 'Refinance' },
  { value: 'investment', label: 'Investment property' },
  { value: 'construction', label: 'Building a home' },
]

export default function LeadCaptureModal({
  open,
  onClose,
  source = 'website',
  context,
  heading,
  blurb,
  submitLabel,
  onSubmitted,
  addressField = false,
  addressLabel = 'Property address',
}: Props) {
  const [name, setName] = useState('')
  const [address, setAddress] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [purpose, setPurpose] = useState('purchase')
  const [notes, setNotes] = useState('')

  const [emailError, setEmailError] = useState<string | null>(null)
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    // Reset to a FRESH form every time the modal opens. Without this, `submitted`
    // stays true after a submit, so reopening shows the thank-you screen with no
    // way to enter new details (and stale field values persist).
    setSubmitted(false)
    setServerError(null)
    setSubmitting(false)
    setName('')
    setAddress('')
    setEmail('')
    setPhone('')
    setPurpose('purchase')
    setEmailError(null)
    setPhoneError(null)
    // Pre-fill the notes with the customer's calculator figures when present.
    setNotes(context ? summariseContext(context) : '')
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = ''
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null

  const canSubmit =
    !!name.trim() &&
    !!email.trim() &&
    !!phone.trim() &&
    (!addressField || !!address.trim()) &&
    !emailError &&
    !phoneError &&
    !submitting

  function validateEmail(v: string) {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setEmailError('Enter a valid email address.')
    } else setEmailError(null)
  }
  function validatePhone(v: string) {
    const digits = v.replace(/\D/g, '')
    if (digits.length < 8) {
      setPhoneError('Enter a valid phone number (min 10 digits).')
    } else setPhoneError(null)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setServerError(null)
    setSubmitting(true)
    try {
      const mergedContext: LeadContext = {
        ...(context ?? {}),
        ...(addressField && address.trim() ? { property_address: address.trim() } : {}),
      }
      const notesOut = [
        addressField && address.trim() ? `Property address: ${address.trim()}` : '',
        notes.trim(),
      ]
        .filter(Boolean)
        .join('\n')
      await api.post<LeadResponse>('/api/leads', {
        customer_name: name.trim(),
        customer_email: email.trim(),
        customer_phone: phone.trim(),
        loan_purpose: purpose,
        notes: notesOut || null,
        source,
        loan_details: mergedContext,
      })
      setSubmitted(true)
      // Fire the caller's hook (e.g. generate + download the results PDF). Guarded
      // so a PDF error can never mask a successful lead capture.
      if (onSubmitted) {
        try {
          onSubmitted({ name: name.trim(), email: email.trim(), phone: phone.trim(), context })
        } catch {
          /* PDF generation is best-effort; the lead is already saved. */
        }
      }
      // No product suggestions on the thank-you by design: implying a lender
      // "suits you" without a licensed assessment is misleading (and regulated
      // credit assistance under the NCCP Act). A broker follows up; live rates
      // remain visible elsewhere on the site.
    } catch (err: unknown) {
      setServerError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-label="Lead capture"
      aria-modal="true"
    >
      <div className="bg-white rounded-2xl shadow-card max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto">
        {submitted ? (
          <div className="py-2">
            <div className="text-center">
              <h2 className="font-heading text-2xl text-ink mb-2">Thank you</h2>
              <p className="text-gs-text-soft mb-6">
                We&apos;ll be in touch shortly. A {tenantConfig.name} broker will call within one business day.
              </p>
            </div>

            <button
              onClick={onClose}
              className="w-full px-6 py-2.5 rounded-lg font-semibold text-sm"
              style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="font-heading text-xl text-ink">{heading ?? 'Talk to a broker'}</h2>
                <p className="text-sm text-gs-text-muted mt-1">
                  {blurb ?? `${tenantConfig.name} · No obligation`}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gs-text-muted hover:text-ink"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {addressField && (
                <div>
                  <label htmlFor="lcm-address" className="block text-sm font-medium mb-1">{addressLabel}</label>
                  <input
                    id="lcm-address"
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="e.g. 12 Example St, Suburb VIC 3000"
                    className="w-full px-3 py-2 border border-[#D8D8D0] rounded-lg text-sm"
                    required
                  />
                </div>
              )}
              <div>
                <label htmlFor="lcm-name" className="block text-sm font-medium mb-1">Full name</label>
                <input
                  id="lcm-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-[#D8D8D0] rounded-lg text-sm"
                  required
                />
              </div>

              <div>
                <label htmlFor="lcm-email" className="block text-sm font-medium mb-1">Email</label>
                <input
                  id="lcm-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={(e) => validateEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-[#D8D8D0] rounded-lg text-sm"
                  aria-invalid={!!emailError}
                  required
                />
                {emailError && <div className="text-red-600 text-xs mt-1">{emailError}</div>}
              </div>

              <div>
                <label htmlFor="lcm-phone" className="block text-sm font-medium mb-1">Phone</label>
                <input
                  id="lcm-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onBlur={(e) => validatePhone(e.target.value)}
                  className="w-full px-3 py-2 border border-[#D8D8D0] rounded-lg text-sm"
                  aria-invalid={!!phoneError}
                  required
                />
                {phoneError && <div className="text-red-600 text-xs mt-1">{phoneError}</div>}
              </div>

              <div>
                <label htmlFor="lcm-purpose" className="block text-sm font-medium mb-1">Loan for / purpose</label>
                <select
                  id="lcm-purpose"
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  className="w-full px-3 py-2 border border-[#D8D8D0] rounded-lg text-sm bg-white"
                >
                  {PURPOSES.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="lcm-notes" className="block text-sm font-medium mb-1">Anything else? (optional)</label>
                <textarea
                  id="lcm-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-[#D8D8D0] rounded-lg text-sm"
                />
              </div>

              {serverError && (
                <div className="text-red-600 text-sm">{serverError}</div>
              )}

              <button
                type="submit"
                disabled={!canSubmit}
                className="w-full py-3 rounded-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: tenantConfig.accentColor, color: tenantConfig.primaryColor }}
              >
                {submitting ? 'Sending…' : (submitLabel ?? `Send to ${tenantConfig.name}`)}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
